import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeSlash } from '@phosphor-icons/react';

export default function PortalLogin() {
  const { login } = useAuth();
  const { lang } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const lb = lang === 'es' ? {
    title: 'Portal de Cliente',
    subtitle: 'Accede al seguimiento de tus estudios',
    email: 'Email', password: 'Contrasena', login: 'Acceder',
    loading: 'Accediendo...', error: 'Email o contrasena incorrectos',
  } : {
    title: 'Client Portal',
    subtitle: 'Access your study tracking dashboard',
    email: 'Email', password: 'Password', login: 'Login',
    loading: 'Logging in...', error: 'Invalid email or password',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error || lb.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="portal-login-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1f3e80] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 border border-white/30 rounded-full" />
          <div className="absolute bottom-32 right-16 w-48 h-48 border border-white/20 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-32 h-32 border border-white/15 rounded-full" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16">
          <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center mb-8">
            <span className="text-white font-bold text-xl font-heading">G</span>
          </div>
          <h1 className="text-4xl font-heading font-bold text-white mb-4 leading-tight">
            GOYA INNOVA
          </h1>
          <p className="text-lg text-white/70 mb-2">Evaluaciones Clinicas</p>
          <p className="text-sm text-white/50 max-w-md leading-relaxed mt-4">
            {lang === 'es'
              ? 'Plataforma de seguimiento en tiempo real de los ensayos clinicos contratados con Laboratorio Goya Innova.'
              : 'Real-time tracking platform for clinical trials contracted with Laboratorio Goya Innova.'}
          </p>
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="flex-1 flex items-center justify-center px-6 bg-[#F9F8F6]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 text-center">
            <div className="w-10 h-10 bg-[#1f3e80] rounded-lg flex items-center justify-center mx-auto mb-3">
              <span className="text-white font-bold text-lg font-heading">G</span>
            </div>
            <h2 className="text-lg font-heading font-semibold text-[#1f3e80]">GOYA INNOVA</h2>
          </div>

          <h2 className="text-2xl font-heading font-semibold text-stone-900 mb-1">{lb.title}</h2>
          <p className="text-sm text-stone-500 mb-8">{lb.subtitle}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-stone-600">{lb.email}</Label>
              <Input
                data-testid="portal-login-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="bg-white border-stone-200 focus:border-[#1f3e80] focus:ring-[#1f3e80]/20"
                placeholder="email@empresa.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-stone-600">{lb.password}</Label>
              <div className="relative">
                <Input
                  data-testid="portal-login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  className="bg-white border-stone-200 pr-10 focus:border-[#1f3e80] focus:ring-[#1f3e80]/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2.5" data-testid="portal-login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1f3e80] hover:bg-[#2a4f8a] text-white font-medium"
              data-testid="portal-login-submit"
            >
              {loading ? lb.loading : lb.login}
            </Button>
          </form>

          <p className="text-[11px] text-stone-400 text-center mt-8">
            Goya Innova — eCRD &copy; {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}
